import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, LogOut, Upload, Mail } from "lucide-react";
import { api } from "../lib/api";

const EMPTY = {
  name: "", description: "", price: 0, category: "borse",
  images: [], in_stock: true, featured: false,
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | product
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const refresh = () => api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  const refreshOrders = () => api.get("/custom-orders").then((r) => setCustomOrders(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setMe(r.data); refresh(); refreshOrders(); })
      .catch(() => { localStorage.removeItem("alimi_token"); nav("/admin/login"); });
  }, []);

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description || "", price: p.price,
      category: p.category, images: p.images || [], in_stock: p.in_stock,
      featured: p.featured,
    });
  };

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024 * 2) {
      toast.error("Immagine troppo grande", { description: "Max 2MB" });
      return;
    }
    const r = new FileReader();
    r.onload = () => setForm((f) => ({ ...f, images: [...f.images, r.result] }));
    r.readAsDataURL(file);
  };

  const removeImg = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("Prodotto creato");
      } else {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Prodotto aggiornato");
      }
      cancel();
      refresh();
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || "" });
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Prodotto eliminato");
      refresh();
    } catch (err) {
      toast.error("Errore eliminazione");
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("alimi_token");
    nav("/admin/login");
  };

  if (!me) return <div className="py-32 text-center text-alimi-inkSoft">Caricamento…</div>;

  return (
    <div data-testid="admin-dashboard" className="max-w-[1300px] mx-auto px-6 lg:px-12 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="overline">Admin</span>
          <h1 className="font-serif text-4xl mt-2 tracking-tight">Dashboard</h1>
          <p className="text-xs text-alimi-inkSoft mt-2">Logged in as {me.email}</p>
        </div>
        <div className="flex gap-3">
          {tab === "products" && (
            <button data-testid="admin-new-btn" onClick={startNew} className="btn-primary"><Plus size={14} /> Nuovo prodotto</button>
          )}
          <button data-testid="admin-logout" onClick={logout} className="btn-outline"><LogOut size={14} /> Esci</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b border-alimi-border">
        <button
          data-testid="tab-products"
          onClick={() => setTab("products")}
          className={`px-5 py-3 text-[0.72rem] tracking-[0.18em] uppercase border-b-2 -mb-px ${tab === "products" ? "border-alimi-ink text-alimi-ink" : "border-transparent text-alimi-inkSoft hover:text-alimi-ink"}`}
        >
          Prodotti ({products.length})
        </button>
        <button
          data-testid="tab-custom-orders"
          onClick={() => { setTab("custom-orders"); refreshOrders(); }}
          className={`px-5 py-3 text-[0.72rem] tracking-[0.18em] uppercase border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "custom-orders" ? "border-alimi-ink text-alimi-ink" : "border-transparent text-alimi-inkSoft hover:text-alimi-ink"}`}
        >
          <Mail size={14} /> Su Misura ({customOrders.length})
        </button>
      </div>

      {tab === "products" && (loading ? (
        <div className="text-center py-20 text-alimi-inkSoft">Caricamento…</div>
      ) : (
        <div className="bg-alimi-surface border border-alimi-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-alimi-surface2 text-left text-[0.7rem] uppercase tracking-[0.15em]">
              <tr>
                <th className="p-4">Immagine</th>
                <th className="p-4">Nome</th>
                <th className="p-4">Categoria</th>
                <th className="p-4">Prezzo</th>
                <th className="p-4">Stato</th>
                <th className="p-4 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-alimi-border" data-testid={`row-${p.id}`}>
                  <td className="p-4">
                    {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-14 h-16 object-cover" /> : <div className="w-14 h-16 bg-alimi-surface2" />}
                  </td>
                  <td className="p-4 font-serif">{p.name}</td>
                  <td className="p-4 capitalize text-alimi-inkSoft">{p.category}</td>
                  <td className="p-4">€ {Number(p.price).toFixed(2)}</td>
                  <td className="p-4">
                    {p.in_stock ? <span className="text-xs text-emerald-700">Disponibile</span> : <span className="text-xs text-alimi-terracotta">Esaurito</span>}
                    {p.featured && <span className="ml-2 text-xs text-alimi-gold">In evidenza</span>}
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <button onClick={() => startEdit(p)} data-testid={`edit-${p.id}`} className="inline-flex items-center gap-1 text-xs hover:text-alimi-gold"><Pencil size={14} /> Modifica</button>
                    <button onClick={() => remove(p)} data-testid={`delete-${p.id}`} className="inline-flex items-center gap-1 text-xs hover:text-alimi-terracotta"><Trash2 size={14} /> Elimina</button>
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr><td colSpan={6} className="p-10 text-center text-alimi-inkSoft">Nessun prodotto.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      {tab === "custom-orders" && (
        <div className="bg-alimi-surface border border-alimi-border overflow-x-auto" data-testid="custom-orders-panel">
          {customOrders.length === 0 ? (
            <div className="p-16 text-center text-alimi-inkSoft">
              <Mail size={32} className="mx-auto text-alimi-gold mb-4" strokeWidth={1.4} />
              <div className="font-serif text-2xl">Nessuna richiesta su misura ancora</div>
              <p className="text-sm mt-2">Le nuove richieste appariranno qui.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-alimi-surface2 text-left text-[0.7rem] uppercase tracking-[0.15em]">
                <tr>
                  <th className="p-4">Data</th>
                  <th className="p-4">Cliente</th>
                  <th className="p-4">Email</th>
                  <th className="p-4">Tipo</th>
                  <th className="p-4">Budget</th>
                  <th className="p-4 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {customOrders.map((o) => (
                  <tr key={o.id} className="border-t border-alimi-border" data-testid={`order-row-${o.id}`}>
                    <td className="p-4 text-alimi-inkSoft text-xs">{new Date(o.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="p-4 font-serif">{o.name}</td>
                    <td className="p-4 text-alimi-inkSoft text-xs">{o.email}</td>
                    <td className="p-4 capitalize">{o.category}</td>
                    <td className="p-4 text-xs">{o.budget || "—"}</td>
                    <td className="p-4 text-right">
                      <button onClick={() => setSelectedRequest(o)} className="text-xs hover:text-alimi-gold" data-testid={`view-order-${o.id}`}>Vedi dettagli</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Custom order detail modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedRequest(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-alimi-bg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8" data-testid="order-detail-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <span className="overline">Richiesta su misura</span>
                <h2 className="font-serif text-3xl mt-2">{selectedRequest.name}</h2>
                <p className="text-xs text-alimi-inkSoft mt-1">{new Date(selectedRequest.created_at).toLocaleString("it-IT")}</p>
              </div>
              <button onClick={() => setSelectedRequest(null)} aria-label="Chiudi"><X /></button>
            </div>

            <div className="space-y-4 text-sm">
              <Row label="Email" value={<a href={`mailto:${selectedRequest.email}`} className="text-alimi-gold hover:underline">{selectedRequest.email}</a>} />
              {selectedRequest.phone && <Row label="Telefono" value={selectedRequest.phone} />}
              <Row label="Categoria" value={<span className="capitalize">{selectedRequest.category}</span>} />
              <Row label="Descrizione" value={<span className="whitespace-pre-line">{selectedRequest.description}</span>} />
              {selectedRequest.color_preference && <Row label="Colori" value={selectedRequest.color_preference} />}
              {selectedRequest.size_preference && <Row label="Taglia/Dimensione" value={selectedRequest.size_preference} />}
              {selectedRequest.budget && <Row label="Budget" value={selectedRequest.budget} />}
              {selectedRequest.desired_date && <Row label="Per quando" value={selectedRequest.desired_date} />}
              {selectedRequest.inspiration_image && (
                <div>
                  <div className="overline mb-2">Immagine ispirazione</div>
                  <img src={selectedRequest.inspiration_image} alt="Ispirazione" className="max-w-xs border border-alimi-border" />
                </div>
              )}
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <a href={`mailto:${selectedRequest.email}?subject=La tua richiesta Alimì su misura`} className="btn-primary">Rispondi via email</a>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={cancel}>
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-form"
            className="bg-alimi-bg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <span className="overline">{editing === "new" ? "Nuovo" : "Modifica"}</span>
                <h2 className="font-serif text-3xl mt-2">{editing === "new" ? "Nuovo prodotto" : editing.name}</h2>
              </div>
              <button type="button" onClick={cancel} aria-label="Chiudi"><X /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="overline block mb-2">Nome</label>
                <input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border outline-none text-sm" />
              </div>
              <div>
                <label className="overline block mb-2">Descrizione</label>
                <textarea data-testid="form-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border outline-none text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Prezzo (€)</label>
                  <input data-testid="form-price" required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border outline-none text-sm" />
                </div>
                <div>
                  <label className="overline block mb-2">Categoria</label>
                  <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-4 py-3 bg-alimi-surface border border-alimi-border outline-none text-sm">
                    <option value="borse">Borse</option>
                    <option value="accessori">Accessori</option>
                    <option value="abbigliamento">Abbigliamento</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Immagini</label>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {form.images.map((src, i) => (
                    <div key={i} className="relative aspect-[3/4] bg-alimi-surface2">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImg(i)} className="absolute -top-2 -right-2 bg-alimi-ink text-white rounded-full p-1"><X size={12} /></button>
                    </div>
                  ))}
                </div>
                <label className="btn-outline cursor-pointer inline-flex">
                  <Upload size={14} /> Carica immagine
                  <input data-testid="form-image-upload" type="file" accept="image/*" onChange={onImage} className="hidden" />
                </label>
                <p className="text-xs text-alimi-inkSoft mt-2">Max 2MB. Le immagini vengono salvate come base64.</p>
              </div>

              <div className="flex gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm">
                  <input data-testid="form-instock" type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} />
                  Disponibile
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input data-testid="form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                  In evidenza
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button type="button" onClick={cancel} className="btn-outline">Annulla</button>
              <button type="submit" className="btn-primary" data-testid="form-save">Salva</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-3 border-b border-alimi-border">
      <div className="overline col-span-1">{label}</div>
      <div className="col-span-2 text-alimi-ink">{value}</div>
    </div>
  );
}
